import clsx from 'clsx'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { match } from 'ts-pattern'

import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import ErrorState from '@/components-latest/shared/ErrorState'
import OrderDetails from '@/components-latest/ui/OrdersPage/OrderDetails'
import { getScrollOrderId } from '@/utils/order-navigation.utils'
import { removeOrderIdFromPending } from '@/ducks/app/slices/app.slice'
import { clearOrderNotifications } from '@/ducks/notifications/slices/notification.slice'
import {
  ordersApi,
  useLazyFetchOrdersListQuery
} from '@/ducks/orders/services/orders.api'
import { resetOrdersStateToInitial } from '@/ducks/orders/slices/orders.slice'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'

import Styles from './NewOrders.module.scss'
const NewOrders = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { lmd_store_web_restaurant_chains_feature } = useFlags()

  const storeId = useSelector(selectStoreId)

  const [
    fetchOrders,
    { data: _orders, isFetching: isOrdersLoading, isError: isOrdersError }
  ] = useLazyFetchOrdersListQuery()

  const orders = _orders?.orders?.filter(order => order.timeLeft !== 0)
  const shouldBeFaded = orders && orders.length > 0 && isOrdersLoading

  useEffect(() => {
    return () => {
      dispatch(resetOrdersStateToInitial())
    }
  }, [dispatch])

  const getOrders = useCallback(() => {
    if (!storeId) return
    fetchOrders({ status: 'NEW_ORDER', store_id: storeId })
  }, [fetchOrders, storeId])

  useEffect(() => {
    getOrders()
  }, [getOrders, storeId])

  const handleTimerEndCreator = (orderId: string) => () => {
    dispatch(ordersApi.util.invalidateTags([{ type: 'Orders', id: 'LIST' }]))
    dispatch(removeOrderIdFromPending({ orderId }))
  }

  const location = useLocation()
  const queryOrderId = getScrollOrderId(location.search)

  useEffect(() => {
    dispatch(clearOrderNotifications())
  }, [dispatch])

  return (
    <div className={clsx(Styles.newOrders, { [Styles.faded]: shouldBeFaded })}>
      {match({ orders, isOrdersLoading, isOrdersError })
        .with(
          { isOrdersLoading: true },
          ({ orders }) => !orders || orders.length === 0,
          () => (
            <div className={Styles.message}>
              <Spinner />
            </div>
          )
        )
        .when(
          ({ orders, isOrdersError }) => isOrdersError && !orders,
          () => (
            <div className={Styles.message}>
              <ErrorState data-testid="new-orders-error" onRetry={getOrders} />
            </div>
          )
        )
        .when(
          ({ orders }) => orders && orders.length === 0,
          () => (
            <div className={Styles.message}>
              <img
                className={Styles.imageForEmpty}
                src="/images/emptyOrders.svg"
                alt="Waiting for new orders"></img>
              <Text variant="title-3" gutter={8}>
                {t('orders.empty-title')}
              </Text>
              <Text variant="subtitle" className={Styles.dimmed}>
                {t('orders.empty-message')}
              </Text>
            </div>
          )
        )
        .when(
          ({ orders }) => orders && orders.length > 0,
          ({ orders }) =>
            orders!.map(order => (
              <OrderDetails
                key={order.id}
                order={order}
                timeLeft={order.timeLeft}
                totalTime={order.timeOut}
                actions={['reject', 'accept']}
                onTimerEnd={handleTimerEndCreator(order.id)}
                onRefresh={getOrders}
                shouldScrollTo={queryOrderId === order.id}
                showBranch={lmd_store_web_restaurant_chains_feature}
              />
            ))
        )
        .otherwise(() => null)}
    </div>
  )
}

export default NewOrders
